/**
 * Custom scrollbar for carousel/slideshow components.
 * Renders a draggable thumb that reflects and controls the scroll position
 * of the nearest parent slideshow-component's scroller.
 */
class CarouselScrollbar extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #thumb = null;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {boolean} */
  #dragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragStartScroll = 0;

  connectedCallback() {
    this.#thumb = this.querySelector('.carousel-scrollbar__thumb');
    this.#track = this.querySelector('.carousel-scrollbar__track');

    const slideshow = this.closest('slideshow-component');
    if (!slideshow) return;

    this.#scroller = slideshow.querySelector('slideshow-slides');
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#track.addEventListener('pointerdown', this.#onTrackPointerDown);
    this.#thumb.addEventListener('pointerdown', this.#onPointerDown);

    // Initial sizing after layout settles
    requestAnimationFrame(() => {
      this.#updateThumbSize();
      this.#onScroll();
    });

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateThumbSize();
      this.#onScroll();
    });
    this.#resizeObserver.observe(this.#scroller);
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('pointerdown', this.#onTrackPointerDown);
    this.#thumb?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#resizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #updateThumbSize = () => {
    if (!this.#scroller || !this.#thumb) return;
    const { clientWidth, scrollWidth } = this.#scroller;
    if (scrollWidth <= clientWidth) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const ratio = clientWidth / scrollWidth;
    this.#thumb.style.width = `${ratio * 100}%`;
  };

  #onScroll = () => {
    if (!this.#scroller || !this.#thumb || !this.#track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#scroller;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;
    const trackWidth = this.#track.clientWidth;
    const thumbWidth = this.#thumb.offsetWidth;
    const maxThumbLeft = trackWidth - thumbWidth;
    const thumbLeft = (scrollLeft / maxScroll) * maxThumbLeft;
    this.#thumb.style.transform = `translateX(${thumbLeft}px)`;
  };

  #onPointerDown = (/** @type {PointerEvent} */ e) => {
    e.preventDefault();
    this.#dragging = true;
    this.#dragStartX = e.clientX;
    this.#dragStartScroll = this.#scroller?.scrollLeft ?? 0;
    this.#thumb?.setPointerCapture(e.pointerId);
    document.addEventListener('pointermove', this.#onPointerMove);
    document.addEventListener('pointerup', this.#onPointerUp);
    this.classList.add('carousel-scrollbar--dragging');
  };

  #onPointerMove = (/** @type {PointerEvent} */ e) => {
    if (!this.#dragging || !this.#scroller || !this.#track || !this.#thumb) return;
    const deltaX = e.clientX - this.#dragStartX;
    const trackWidth = this.#track.clientWidth;
    const thumbWidth = this.#thumb.offsetWidth;
    const maxThumbLeft = trackWidth - thumbWidth;
    const { scrollWidth, clientWidth } = this.#scroller;
    const maxScroll = scrollWidth - clientWidth;
    const scrollDelta = (deltaX / maxThumbLeft) * maxScroll;
    this.#scroller.scrollLeft = this.#dragStartScroll + scrollDelta;
  };

  #onPointerUp = (/** @type {PointerEvent} */ e) => {
    this.#dragging = false;
    this.#thumb?.releasePointerCapture(e.pointerId);
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    this.classList.remove('carousel-scrollbar--dragging');
  };

  #onTrackPointerDown = (/** @type {PointerEvent} */ e) => {
    if (e.target === this.#thumb) return;
    if (!this.#scroller || !this.#track || !this.#thumb) return;

    const trackRect = this.#track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const thumbWidth = this.#thumb.offsetWidth;
    const trackWidth = this.#track.clientWidth;
    const maxThumbLeft = trackWidth - thumbWidth;
    const targetThumbLeft = clickX - thumbWidth / 2;
    const ratio = Math.max(0, Math.min(targetThumbLeft / maxThumbLeft, 1));
    const { scrollWidth, clientWidth } = this.#scroller;
    const maxScroll = scrollWidth - clientWidth;
    this.#scroller.scrollLeft = ratio * maxScroll;
  };
}

customElements.define('carousel-scrollbar', CarouselScrollbar);
